import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import {
  Buildings,
  EnvelopeSimple,
  Eye,
  EyeSlash,
  Fingerprint,
  Key,
  LockKey,
  Scan,
  ShieldCheck,
} from '@phosphor-icons/react';
import { Spinner, toast } from '@helios/ui';
import { ConstructionOsLogo } from '@/components/brand/ConstructionOsLogo';
import { env } from '@/lib/env';
import {
  authClient,
  isWorkspaceSelection,
  type AuthEnvironments,
  type DemoAccess,
  type WorkspaceOption,
} from '@/services/authClient';
import { useAuth } from './authContext';
import { useUserPreferences } from '@/features/settings/UserPreferencesProvider';
import { explicitAuthReturnPath, preferredPostSignInPath, type AuthReturnLocation } from './postSignInPath';
import './auth-login.css';

interface RedirectState {
  from?: AuthReturnLocation;
}

type PendingAction = 'password' | 'passkey' | 'demo' | null;

const trustItems = [
  { icon: ShieldCheck, label: 'Zero Trust' },
  { icon: Key, label: 'AES-256' },
  { icon: Fingerprint, label: 'Biometric MFA' },
  { icon: Scan, label: 'AI Threat Detection' },
] as const;

export function AuthPage() {
  const loginSurfaceRef = useRef<HTMLElement>(null);
  const backgroundVideoRef = useRef<HTMLVideoElement>(null);
  const auth = useAuth();
  const ownPreferences = useUserPreferences();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(env.isDev ? 'admin@helios.local' : '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [platformAuthenticatorReady, setPlatformAuthenticatorReady] = useState(false);
  const [enrollAfterSignIn, setEnrollAfterSignIn] = useState(false);
  const [enrollmentActive, setEnrollmentActive] = useState(false);
  const [demoAccess, setDemoAccess] = useState<DemoAccess | null>(null);
  // Set when one address opens several workspaces; the password is already
  // verified against every option shown.
  const [workspaceChoices, setWorkspaceChoices] = useState<WorkspaceOption[] | null>(null);
  const [environments, setEnvironments] = useState<AuthEnvironments | null>(null);
  const [environmentPrompt, setEnvironmentPrompt] = useState(false);

  const state = location.state as RedirectState | null;
  const explicitReturn = explicitAuthReturnPath(state?.from);
  const redirectTo = explicitReturn ?? preferredPostSignInPath(ownPreferences.preferences?.landingPage, auth.permissions);

  useEffect(() => {
    const supported = window.isSecureContext && typeof window.PublicKeyCredential !== 'undefined';
    setPasskeySupported(supported);
    if (
      supported &&
      typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function'
    ) {
      void PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(setPlatformAuthenticatorReady)
        .catch(() => setPlatformAuthenticatorReady(false));
    }
  }, []);

  // Ambient background video: honor the OS "reduce motion" preference by holding
  // on the poster frame instead of looping the clip. Muted autoplay is otherwise
  // permitted by every major browser; if a browser still blocks it, the poster
  // simply stays visible — nothing on the page depends on playback.
  useEffect(() => {
    const video = backgroundVideoRef.current;
    if (!video || typeof window.matchMedia !== 'function') return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const applyMotionPreference = (event?: MediaQueryListEvent) => {
      const reduced = event ? event.matches : reduceMotion.matches;
      if (reduced) {
        video.pause();
      } else if (event) {
        void video.play()?.catch(() => undefined);
      }
    };

    applyMotionPreference();
    reduceMotion.addEventListener?.('change', applyMotionPreference);
    return () => reduceMotion.removeEventListener?.('change', applyMotionPreference);
  }, []);

  useEffect(() => {
    let active = true;
    void authClient.getDemoAccess().then((result) => {
      if (active) setDemoAccess(result);
    });
    void authClient.getEnvironments().then((result) => {
      if (active) setEnvironments(result);
    });
    return () => {
      active = false;
    };
  }, []);

  // Deployments this operator can move to other than the one serving this page.
  const alternateEnvironments = (environments?.environments ?? []).filter((item) => !item.current);
  const currentEnvironment =
    (environments?.environments ?? []).find((item) => item.current) ?? null;

  // The environment prompt renders *after* authentication succeeds, so the
  // usual "already signed in, go away" redirect has to stand down for it.
  if (auth.authenticated && !enrollmentActive && !environmentPrompt) {
    if (auth.accessMode === 'COMPANY_SETUP') return <Navigate to="/company-setup" replace />;
    if (auth.accessMode === 'PASSWORD_CHANGE_REQUIRED') return <Navigate to="/auth/password-change" replace state={state} />;
    if (!explicitReturn && ownPreferences.loading) return <main className="grid min-h-screen place-items-center bg-background"><Spinner /></main>;
    return <Navigate to={redirectTo} replace />;
  }

  /**
   * One sign-in attempt. `chosenTenantSlug` is supplied only on the second pass,
   * after the operator has picked from the workspace list — the first pass
   * deliberately sends no workspace so the server resolves it from the credential.
   */
  const attemptSignIn = async (chosenTenantSlug?: string) => {
    setError(null);
    setPendingAction('password');
    const shouldEnroll = enrollAfterSignIn && passkeySupported;
    setEnrollmentActive(shouldEnroll);

    const result = await auth.signIn({ email, password, tenantSlug: chosenTenantSlug });

    if (isWorkspaceSelection(result)) {
      setWorkspaceChoices(result.workspaces);
      setEnrollmentActive(false);
      setPendingAction(null);
      return;
    }

    if (!result.authenticated) {
      const message = result.message ?? 'Invalid email or password';
      setEnrollmentActive(false);
      setPendingAction(null);
      setError(message);
      toast.error('Sign-in failed', { description: message });
      return;
    }

    setWorkspaceChoices(null);

    if (result.accessMode === 'PASSWORD_CHANGE_REQUIRED') {
      setPendingAction(null); setEnrollmentActive(false);
      navigate('/auth/password-change', { replace: true, state }); return;
    }

    if (shouldEnroll) {
      const enrollment = await auth.enrollPasskey();
      if (enrollment.registered) {
        toast.success('Passkey created', {
          description:
            'Future sign-ins can use your face, fingerprint, device PIN, or security key.',
        });
      } else {
        toast.warning('Signed in without a passkey', {
          description: enrollment.message ?? 'You can try passkey setup again later.',
        });
      }
    } else {
      toast.success('Signed in', {
        description: `${result.user?.name} · ${result.tenant?.name}`,
      });
    }

    setPendingAction(null);
    setEnrollmentActive(false);

    // Only interrupt with a chooser when there is somewhere else to go.
    if (alternateEnvironments.length > 0) {
      setEnvironmentPrompt(true);
      return;
    }
    // The authenticated branch waits for own preferences before the default redirect.
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await attemptSignIn();
  };

  const signInWithPasskey = async () => {
    if (!email.trim()) {
      setError('Enter your email before using a passkey.');
      return;
    }
    if (!passkeySupported) {
      setError('Passkeys require a supported browser on HTTPS or localhost.');
      return;
    }

    setError(null);
    setPendingAction('passkey');
    const result = await auth.signInWithPasskey(email.trim());
    setPendingAction(null);
    if (!result.authenticated) {
      const message = result.message ?? 'Passkey sign-in failed';
      setError(message);
      toast.error('Passkey sign-in failed', { description: message });
      return;
    }

    toast.success('Identity verified', {
      description: `${result.user?.name} · ${result.tenant?.name}`,
    });
    if (result.accessMode === 'PASSWORD_CHANGE_REQUIRED') navigate('/auth/password-change', { replace: true, state });
  };

  const signInDemo = async () => {
    setError(null);
    setPendingAction('demo');
    const result = await auth.signInDemo();
    setPendingAction(null);
    if (!result.authenticated) {
      const message = result.message ?? 'Demo workspace is unavailable';
      setError(message);
      toast.error('Demo access unavailable', { description: message });
      return;
    }
    toast.success('Demo workspace ready', {
      description: 'Residential, commercial, and home-improvement projects are loaded.',
    });
    // The authenticated branch resolves the preferred landing page.
  };

  const isBusy = auth.isLoading || pendingAction !== null;

  const updateSurfaceDepth = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') {
      return;
    }

    const horizontal = (event.clientX / window.innerWidth - 0.5) * 12;
    const vertical = (event.clientY / window.innerHeight - 0.5) * 10;
    const surface = loginSurfaceRef.current;
    surface?.style.setProperty('--auth-bg-x', `${horizontal * -0.38}px`);
    surface?.style.setProperty('--auth-bg-y', `${vertical * -0.38}px`);
    surface?.style.setProperty('--auth-panel-x', `${horizontal * 0.18}px`);
    surface?.style.setProperty('--auth-panel-y', `${vertical * 0.18}px`);
  };

  const resetSurfaceDepth = () => {
    const surface = loginSurfaceRef.current;
    surface?.style.setProperty('--auth-bg-x', '0px');
    surface?.style.setProperty('--auth-bg-y', '0px');
    surface?.style.setProperty('--auth-panel-x', '0px');
    surface?.style.setProperty('--auth-panel-y', '0px');
  };

  return (
    <main
      ref={loginSurfaceRef}
      className="auth-login"
      onPointerMove={updateSurfaceDepth}
      onPointerLeave={resetSurfaceDepth}
    >
      <div className="auth-login__background" aria-hidden="true">
        <video
          ref={backgroundVideoRef}
          className="auth-login__video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/auth/login-bg.png"
          disablePictureInPicture
          disableRemotePlayback
          tabIndex={-1}
          data-testid="auth-login-video"
        >
          <source src="/auth/login-bg.mp4" type="video/mp4" />
        </video>
      </div>

      {/*
        No mark over the artwork. The lockup already sits inside the sign-in
        card a few hundred pixels away, so a second one floating on the
        photograph was the same word twice — and it competed with the image
        rather than sitting on it.
      */}

      <div className="auth-login__layout">
        <section
          className="auth-login__panel"
          aria-labelledby="auth-title"
          aria-busy={isBusy}
          data-busy={isBusy ? '' : undefined}
        >
          <div className="auth-login__panel-inset">
            <header className="auth-login__header">
              <ConstructionOsLogo variant="auth" />
              <h1 id="auth-title" className="sr-only">
                HELIOS Construction OS secure access
              </h1>
            </header>

            {environmentPrompt ? (
              <div className="auth-login__form" role="group" aria-labelledby="auth-env-title">
                <p id="auth-env-title" className="auth-login__hint">
                  Signed in as {auth.user?.email}. Choose an environment.
                </p>
                {currentEnvironment ? (
                  <button
                    type="button"
                    className="auth-login__button"
                    onClick={() => setEnvironmentPrompt(false)}
                  >
                    <ShieldCheck aria-hidden="true" />
                    <span>Continue in {currentEnvironment.label}</span>
                  </button>
                ) : null}
                {alternateEnvironments.map((item) => (
                  <a
                    key={item.key}
                    className="auth-login__button auth-login__button--demo"
                    href={item.url}
                  >
                    <Buildings aria-hidden="true" />
                    <span>Switch to {item.label}</span>
                  </a>
                ))}
                <p className="auth-login__hint">
                  Production and demo are separate deployments holding separate data. Switching
                  opens the other one, where you sign in again.
                </p>
              </div>
            ) : workspaceChoices ? (
              <div className="auth-login__form" role="group" aria-labelledby="auth-workspace-title">
                <p id="auth-workspace-title" className="auth-login__hint">
                  This email opens more than one workspace. Choose where to continue.
                </p>
                {workspaceChoices.map((workspace) => (
                  <button
                    key={workspace.tenantId}
                    type="button"
                    className="auth-login__button"
                    disabled={isBusy}
                    onClick={() => void attemptSignIn(workspace.tenantSlug)}
                  >
                    {pendingAction === 'password' ? (
                      <Spinner className="auth-login__spinner" aria-hidden="true" />
                    ) : (
                      <Buildings aria-hidden="true" />
                    )}
                    <span>{workspace.tenantName}</span>
                  </button>
                ))}
                <button
                  type="button"
                  className="auth-login__button auth-login__button--demo"
                  disabled={isBusy}
                  onClick={() => setWorkspaceChoices(null)}
                >
                  <span>Use a different email</span>
                </button>
              </div>
            ) : (
              <form className="auth-login__form" onSubmit={submit}>
                <label className="auth-login__field">
                  <span className="auth-login__label">
                    <EnvelopeSimple aria-hidden="true" />
                    Email
                  </span>
                  <input
                    className="auth-login__input"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.currentTarget.value)}
                    autoComplete="username webauthn"
                    disabled={isBusy}
                    required
                  />
                </label>

                <label className="auth-login__field">
                  <span className="auth-login__label">
                    <LockKey aria-hidden="true" />
                    Password
                  </span>
                  <span className="auth-login__password">
                    <input
                      className="auth-login__input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(event) => setPassword(event.currentTarget.value)}
                      autoComplete="current-password"
                      disabled={isBusy}
                      required
                    />
                    <button
                      type="button"
                      className="auth-login__reveal"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((value) => !value)}
                      disabled={isBusy}
                    >
                      {showPassword ? <EyeSlash aria-hidden="true" /> : <Eye aria-hidden="true" />}
                    </button>
                  </span>
                </label>

                {error ? (
                  <p role="alert" className="auth-login__error">
                    {error}
                  </p>
                ) : null}

                <button
                  type="submit"
                  className="auth-login__button auth-login__button--primary"
                  disabled={isBusy}
                >
                  {pendingAction === 'password' ? (
                    <Spinner className="auth-login__spinner" aria-hidden="true" />
                  ) : null}
                  <span>{enrollAfterSignIn ? 'Sign in & create passkey' : 'Sign in'}</span>
                </button>

                <div className="auth-login__divider" aria-hidden="true">
                  <span>or</span>
                </div>

                <button
                  type="button"
                  className="auth-login__button auth-login__button--passkey"
                  disabled={isBusy || !passkeySupported}
                  onClick={() => void signInWithPasskey()}
                >
                  {pendingAction === 'passkey' ? (
                    <Spinner className="auth-login__spinner" aria-hidden="true" />
                  ) : (
                    <Fingerprint aria-hidden="true" />
                  )}
                  <span>Use a passkey</span>
                </button>

                <label className="auth-login__enroll">
                  <input
                    type="checkbox"
                    checked={enrollAfterSignIn}
                    onChange={(event) => setEnrollAfterSignIn(event.currentTarget.checked)}
                    disabled={isBusy || !passkeySupported}
                  />
                  <span>Set up face, fingerprint, device PIN, or security key after sign-in</span>
                </label>

                <p className="auth-login__capability" aria-live="polite">
                  <span
                    className="auth-login__capability-dot"
                    data-ready={passkeySupported ? '' : undefined}
                    aria-hidden="true"
                  />
                  {platformAuthenticatorReady
                    ? 'Device biometrics and passkeys ready'
                    : passkeySupported
                      ? 'Passkeys and security keys supported'
                      : 'Password access available'}
                </p>

                <Link to="/company-setup" className="auth-login__button auth-login__button--company-setup">
                  <Buildings aria-hidden="true" />
                  <span>Set up your company</span>
                </Link>

                {demoAccess?.enabled ? (
                  <button
                    type="button"
                    className="auth-login__button auth-login__button--demo"
                    disabled={isBusy}
                    onClick={() => void signInDemo()}
                  >
                    {pendingAction === 'demo' ? (
                      <Spinner className="auth-login__spinner" aria-hidden="true" />
                    ) : (
                      <Buildings aria-hidden="true" />
                    )}
                    <span>Enter demo workspace</span>
                  </button>
                ) : demoAccess?.externalUrl ? (
                  <a
                    className="auth-login__button auth-login__button--demo"
                    href={demoAccess.externalUrl}
                  >
                    <Buildings aria-hidden="true" />
                    <span>Open isolated demo</span>
                  </a>
                ) : null}
              </form>
            )}

            <div className="auth-login__trust" aria-label="Security controls">
              {trustItems.map(({ icon: Icon, label }) => (
                <div className="auth-login__trust-item" key={label}>
                  <Icon className="auth-login__trust-icon" weight="thin" aria-hidden="true" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
